import React, {
  useEffect,
  useRef,
  useState,
} from 'react';
import { VariableSizeList, areEqual } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';

import { Box } from '@styleseat/ui';

import { useIsMobile } from '../../../../hooks';
import ContactListItem from '../ContactListItem';
import { IProviderClient } from '../../../../api/Providers/Clients/types';
import { PublicProviderProfile } from '../../../../modules/Api.types';
import ContactListEducationalBanner from './ContactListEducationalBanner';
import { ContactListGroupHeader } from './ContactListGroupHeader';
import { Stack } from '../../../ui';
import { EmptyClientListContainer } from '../EmptyClientList/EmptyClientListContainer';

enum ListRowHeight {
  Banner = 53,
  Contact = 68,
  Header = 30,
  EmptyState = 674,
}

const EMPTY_OBJECT = {};
const EMPTY_ARRAY = [];

/**
 * Returns the first letter of a contact's name, lowercased
 * @param contact
 * @returns {string}
 */
function getFirstLetter(contact: IProviderClient) {
  return contact.name.charAt(0).toLowerCase();
}

/**
 * Return true if it is the first contact or when the contact should start a new group.
 * @param {Object} contact - contact item provided by the repeater
 * @param {Object | string} previous - preceding contact item provided by the repeater
 */
function showContactGroupLetter(contact: IProviderClient, previous?: string | null) {
  return contact && (!previous || previous !== getFirstLetter(contact));
}

export type ContactListProps = {
  searchText?: string;
  contacts: IProviderClient[];
  recentContacts?: IProviderClient[];
  isSearchingByPhoneNumber: boolean;
  provider: Pick<PublicProviderProfile, 'id' | 'creation_time' | 'profile_phase_one_complete_date'>;
  selectedContactId: number,
  showInviteActions: boolean,
  source: string | null,
  invitedClients: { [key: number]: boolean; id?: boolean },
  onNewClientSaved: (newClient: IProviderClient) => void;
  onContactSelect: (contact: IProviderClient) => void;
  // whether or not to allow selection of this contact
  isContactDisabled: (contact: IProviderClient) => boolean;
  // invoked when tapping the info icon on a disabled contact
  onDisabledContactInfo?: (contact: IProviderClient) => void;
  onClientInvited: (id: number) => void;
  onRecentContactsViewed: (contactIds: Array<number>) => void;
  viewedAddedList: Array<number>;
  showEmptyState: boolean;
  borderRadius?: number;
};

type ListItemHeader = {
  type: 'header';
  item: string;
};

type ListItemBanner = {
  type: 'banner';
  item: ((props: { index: number; style: any }) => JSX.Element);
};

type ListItemContact = {
  type: 'contact';
  isRecent: boolean;
  item: IProviderClient;
};

type ListItemEmptyState = {
  type: 'empty',
};

type ListItem = {
  height: number;
  shouldAnimate?: boolean;
  animationDelay?: number;
} & (ListItemContact | ListItemBanner | ListItemHeader | ListItemEmptyState);

const ContactList = ({
  searchText,
  contacts,
  recentContacts = EMPTY_ARRAY,
  isContactDisabled,
  isSearchingByPhoneNumber,
  provider,
  selectedContactId,
  showInviteActions,
  source,
  invitedClients = EMPTY_OBJECT,
  onClientInvited,
  onRecentContactsViewed,
  viewedAddedList,
  showEmptyState,
  onNewClientSaved,
  onContactSelect,
  onDisabledContactInfo,
  borderRadius = 0,
}: ContactListProps) => {
  const [shouldShowList, setShouldShowList] = useState(false);
  const [listItems, setListItems] = useState<ListItem[]>([]);
  const listRef = useRef<VariableSizeList>();
  const isMobile = useIsMobile();

  useEffect(() => {
    // Reset section headings when component re-renders
    let letter: string | undefined | null;

    // only populate the contacts list once we get this data
    if (
      viewedAddedList === null
      || !provider
      || !contacts
    ) {
      return;
    }

    const newList: ListItem[] = [];
    const viewedList: number[] = [];

    if (recentContacts.length > 0) {
      newList.push({
        type: 'header',
        item: 'RECENTLY ADDED',
        height: ListRowHeight.Header,
      });
    }

    recentContacts.forEach((contact, index) => {
      viewedList.push(contact.id);
      newList.push({
        type: 'contact',
        item: contact,
        height: ListRowHeight.Contact,
        isRecent: true,
        shouldAnimate: !viewedAddedList || !viewedAddedList.includes(contact.id),
        animationDelay: (recentContacts.length - (recentContacts.length - index)) * 200,
      });
    });

    newList.push({
      type: 'banner',
      height: ListRowHeight.Banner,
      item: ContactListEducationalBanner,
    });

    contacts.forEach(contact => {
      const isNewLetter = showContactGroupLetter(contact, letter);
      letter = isNewLetter ? getFirstLetter(contact) : letter;
      if (isNewLetter && letter) {
        newList.push({
          item: letter,
          type: 'header',
          height: ListRowHeight.Header,
        });
      }
      newList.push({
        item: contact,
        type: 'contact',
        isRecent: false,
        height: ListRowHeight.Contact,
      } as ListItem);
    });

    if (showEmptyState && contacts.length) {
      newList.push({
        type: 'empty',
        height: ListRowHeight.EmptyState,
      });
    }

    setListItems(newList);
    if (!viewedAddedList
      || (viewedAddedList.length !== viewedList.length
        && viewedAddedList[0] !== viewedList[0])) {
      onRecentContactsViewed(viewedList);
    }

    listRef?.current?.resetAfterIndex(0);

    setShouldShowList(!!contacts.length);
  }, [
    contacts,
    viewedAddedList,
    provider,
    recentContacts,
    listRef,
    onRecentContactsViewed,
    showEmptyState,
  ]);

  // "areEqual" is used with React.memo here so that it knows to "compare
  // individual style props and ignore the wrapper object in order to avoid
  // unnecessarily re-rendering when cached style objects are reset".
  // more info: https://react-window.now.sh/#/examples/list/memoized-list-items
  const ListRow = React.memo(({
    index,
    style,
  }: { index: number; style: any }) => {
    const listItem = listItems[index];

    if (listItem.type === 'banner') {
      const Banner = listItem.item;
      return (
        <Banner index={index} style={style} />
      );
    }

    if (listItem.type === 'header') {
      return (
        <ContactListGroupHeader text={listItem.item} style={style} />
      );
    }

    if (listItem.type === 'empty') {
      return (
        <EmptyClientListContainer
          providerId={provider?.id}
          onNewClientSaved={onNewClientSaved}
          style={style}
        />
      );
    }

    const contact = listItem.item;

    // if the contact has already been invited to StyleSeat, change CTA to INVITE TO BOOK
    const hasBeenInvitedBefore = invitedClients?.[contact.id];

    return (
      <Box style={style}>
        <ContactListItem
          provider={provider}
          contact={contact}
          selectContact={onContactSelect}
          selected={contact.id === Number(selectedContactId)}
          isSearchingByPhoneNumber={isSearchingByPhoneNumber}
          searchTerm={searchText}
          disabled={isContactDisabled(contact)}
          disabledContactInfo={onDisabledContactInfo}
          showInviteActions={showInviteActions}
          hasBeenInvitedBefore={hasBeenInvitedBefore}
          onInviteClick={onClientInvited}
          isRecent={listItem.isRecent}
          shouldAnimate={listItem.shouldAnimate}
          animationDelay={listItem.animationDelay}
          trackingParams={{
            type: listItem.isRecent ? 'recent' : 'all',
            client_id: contact.id,
            source,
          }}
        />
      </Box>
    );
  }, areEqual);

  const getRowSize = (index: number) => listItems[index].height;

  return (
    <Stack
      flex={1}
      borderRadius={isMobile ? 0 : borderRadius}
      showBoxShadow
      overflow="hidden"
    >
      <Stack flex={1}>
        {shouldShowList && (
          <Box
            testID="contact-list-all"
            minWidth="100%"
            flexGrow={1}
          >
            <AutoSizer defaultHeight={600} defaultWidth={340}>
              {({
                height,
                width,
              }) => (
                <VariableSizeList
                  ref={listRef}
                  height={height}
                  itemCount={listItems.length}
                  itemSize={getRowSize}
                  width={width}
                >
                  {ListRow}
                </VariableSizeList>
              )}
            </AutoSizer>
          </Box>
        )}
      </Stack>
    </Stack>
  );
};

export default ContactList;
